import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import api from '../api/axiosConfig';
import { Html5Qrcode } from 'html5-qrcode';
import './CadastroPage.css';
import Footer from './Footer';

const CadastroPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    // Fetch studios for the logged-in applicator
    const fetchEstudios = async () => {
      try {
        const resp = await api.get('/api/estudios');
        console.log('[CadastroPage] GET /api/estudios response:', resp.data);
        if (resp.data.success) setEstudios(resp.data.estudios);
      } catch (err) { console.error('Erro ao carregar estúdios:', err); }
    };
    fetchEstudios();
  }, [navigate]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.product-search-container')) {
        setShowProductSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [formData, setFormData] = useState({
    nomeCliente: '',
    telefone: '',
    email: '',
    cpf: '',
    dataNascimento: '',
    endereco: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    modeloCarro: '',
    anoModelo: '',
    cor: '',
    placa: '',
    produtosAplicados: [],
    serialNumbers: {},
    dataAplicacao: '',
    localAplicado: '',
    estado: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  const [placaSearch, setPlacaSearch] = useState('');
  const [searchingPlaca, setSearchingPlaca] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [tutorialOpen, setTutorialOpen] = useState(false);

  // Studios state
  const [estudios, setEstudios] = useState([]);

  // Barcode scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerProduct, setScannerProduct] = useState('');
  const [scannerError, setScannerError] = useState('');
  const html5QrCodeRef = useRef(null);
  const scannerContainerId = 'barcode-scanner-container';

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING
          await html5QrCodeRef.current.stop();
        }
      } catch (e) {
        // ignore
      }
      html5QrCodeRef.current = null;
    }
  }, []);

  const openScanner = (product) => {
    setScannerProduct(product);
    setScannerOpen(true);
    setScannerError('');
  };

  const closeScanner = useCallback(async () => {
    await stopScanner();
    setScannerOpen(false);
    setScannerProduct('');
    setScannerError('');
  }, [stopScanner]);

  // Start scanner when modal opens
  useEffect(() => {
    if (!scannerOpen) return;

    const startScanner = async () => {
      // Small delay to ensure DOM is ready
      await new Promise(r => setTimeout(r, 300));

      const container = document.getElementById(scannerContainerId);
      if (!container) return;

      try {
        const html5QrCode = new Html5Qrcode(scannerContainerId);
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 280, height: 120 },
            aspectRatio: 1.0,
            formatsToSupport: [
              0,  // QR_CODE
              4,  // CODE_128
              2,  // CODE_39
              3,  // CODE_93
              1,  // AZTEC
              10, // EAN_13
              9,  // EAN_8
              6,  // ITF
              8,  // UPC_A
              12, // UPC_E
              5,  // CODABAR
              11, // DATA_MATRIX
            ],
          },
          (decodedText) => {
            // Success - fill the serial number
            setFormData(prev => ({
              ...prev,
              serialNumbers: {
                ...prev.serialNumbers,
                [scannerProduct]: decodedText
              }
            }));
            closeScanner();
          },
          () => {
            // ignore scan failures (no code found yet)
          }
        );
      } catch (err) {
        console.error('Erro ao iniciar scanner:', err);
        setScannerError(t('cadastro.scannerError'));
      }
    };

    startScanner();

    return () => {
      stopScanner();
    };
  }, [scannerOpen, scannerProduct, closeScanner, stopScanner]);

  // CPF search state
  const [searchingCPF, setSearchingCPF] = useState(false);
  const [clienteExistente, setClienteExistente] = useState(false);
  const [veiculosExistentes, setVeiculosExistentes] = useState([]);
  const [dadosBloqueados, setDadosBloqueados] = useState(false);

  // CEP lookup state
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  const formatCEP = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
  };

  const handleCepChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, cep: formatted }));
    setCepError('');
  };

  const buscarCep = async () => {
    const digits = formData.cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await resp.json();
      if (data.erro) {
        setCepError(t('cadastro.cepError'));
      } else {
        setFormData(prev => ({
          ...prev,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || prev.estado
        }));
      }
    } catch (err) {
      console.error('[CEP] Erro:', err);
      setCepError(t('cadastro.cepError'));
    } finally {
      setCepLoading(false);
    }
  };

  // Search client by CPF
  const handleCPFSearch = async (cpfValue) => {
    const cpf = cpfValue || formData.cpf;
    if (!cpf || cpf.replace(/[^0-9]/g, '').length < 11) return;

    setSearchingCPF(true);
    setMessage('');

    try {
      const response = await api.post('/api/buscar-cliente-cpf', { cpf });

      if (response.data.success && response.data.found) {
        const { dadosPessoais, veiculos, totalCertificados } = response.data;
        
        setFormData(prev => ({
          ...prev,
          nomeCliente: dadosPessoais.nomeCliente,
          cpf: dadosPessoais.cpf,
          dataNascimento: dadosPessoais.dataNascimento,
          telefone: dadosPessoais.telefone,
          email: dadosPessoais.email,
          endereco: dadosPessoais.endereco,
          estado: dadosPessoais.estado,
          // Vehicle fields stay empty for new vehicle
          modeloCarro: '',
          cor: '',
          placa: '',
          produtosAplicados: [],
          serialNumber: '',
          dataAplicacao: '',
          localAplicado: ''
        }));

        setClienteExistente(true);
        setVeiculosExistentes(veiculos);
        setDadosBloqueados(true);
        setMessage(t('cadastro.clientFound').replace('{{certs}}', totalCertificados).replace('{{vehicles}}', veiculos.length));
      } else {
        setClienteExistente(false);
        setVeiculosExistentes([]);
        setDadosBloqueados(false);
      }
    } catch (error) {
      console.error('Erro ao buscar CPF:', error);
    } finally {
      setSearchingCPF(false);
    }
  };

  // Reset client data to allow editing
  const handleNovoCliente = () => {
    setClienteExistente(false);
    setVeiculosExistentes([]);
    setDadosBloqueados(false);
    setFormData({
      nomeCliente: '', telefone: '', email: '', cpf: '',
      dataNascimento: '', endereco: '', cep: '', logradouro: '', numero: '',
      complemento: '', bairro: '', cidade: '', modeloCarro: '', anoModelo: '', cor: '',
      placa: '', produtosAplicados: [], serialNumbers: {},
      dataAplicacao: '', localAplicado: '', estado: ''
    });
    setCepError('');
    setMessage('');
  };

  // Fill form with existing vehicle data
  const handleSelectVeiculo = (veiculo) => {
    setFormData(prev => ({
      ...prev,
      modeloCarro: veiculo.modeloCarro,
      anoModelo: veiculo.anoModelo || '',
      cor: veiculo.cor,
      placa: veiculo.placa
    }));
    setPlacaSearch(veiculo.placa);
    setMessage(t('cadastro.vehicleDataFilled'));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setMessage(t('cadastro.fileTooLarge'));
        return;
      }
      
      // Check file type
      if (!file.type.match('image.*')) {
        setMessage(t('cadastro.fileTypeError'));
        return;
      }
      
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleProductToggle = (product) => {
    setFormData(prev => ({
      ...prev,
      produtosAplicados: prev.produtosAplicados.includes(product)
        ? prev.produtosAplicados.filter(p => p !== product)
        : [...prev.produtosAplicados, product]
    }));
  };

  const availableProducts = [
    'Ecocoat Kenzo',
    'Ecocoat Arcane', 
    'Ecocoat Elixir',
    'Ecocoat Quartz',
    'Ecocoat Ez',
    'Ecocoat Window',
    'Ecocoat Wheel',
    'Ecocoat Leather',
    'Ecocoat Trim',
    'Ecocoat Eclipse',
    'Ecocoat Premier'
  ];

  const filteredProducts = availableProducts.filter(product => 
    product.toLowerCase().includes(productSearch.toLowerCase()) &&
    !formData.produtosAplicados.includes(product)
  );

  const handleProductSearch = (e) => {
    const value = e.target.value;
    setProductSearch(value);
    setShowProductSuggestions(value.length > 0);
  };

  const handleProductSelect = (product) => {
    setFormData(prev => ({
      ...prev,
      produtosAplicados: [...prev.produtosAplicados, product]
    }));
    setProductSearch('');
    setShowProductSuggestions(false);
  };

  const handleRemoveProduct = (product) => {
    setFormData(prev => {
      const newSerials = { ...prev.serialNumbers };
      delete newSerials[product];
      return {
        ...prev,
        produtosAplicados: prev.produtosAplicados.filter(p => p !== product),
        serialNumbers: newSerials
      };
    });
  };

  const handlePlacaSearch = async (placaParam) => {
    const placa = placaParam || placaSearch;
    if (!placa.trim()) return;

    setSearchingPlaca(true);
    setMessage('');

    try {
      const response = await api.post('/api/buscar-veiculo-por-placa', { placa });

      if (response.data.success) {
        const veiculoData = response.data.data;
        setFormData(prev => ({
          ...prev,
          modeloCarro: veiculoData.modeloCarro || prev.modeloCarro,
          anoModelo: veiculoData.ano || prev.anoModelo,
          cor: veiculoData.cor || prev.cor,
          placa: placa.toUpperCase()
        }));
        setMessage(t('cadastro.vehicleFound'));
      } else {
        setMessage(`❌ ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erro ao buscar veículo:', error);
      const msg = error.response?.data?.message || t('cadastro.vehicleError');
      setMessage(`❌ ${msg}`);
    } finally {
      setSearchingPlaca(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Compose the final "endereco" string from the CEP-derived fields
      // (skip if using a locked/existing client's address, which is already a single string)
      const enderecoFinal = dadosBloqueados
        ? formData.endereco
        : [
            [formData.logradouro, formData.numero].filter(Boolean).join(', '),
            formData.complemento,
            formData.bairro,
            formData.cidade
          ].filter(Boolean).join(' - ');

      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'produtosAplicados') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === 'serialNumbers') {
          formDataToSend.append('serialNumber', JSON.stringify(formData[key]));
        } else if (key === 'endereco') {
          formDataToSend.append('endereco', enderecoFinal);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Validate products selection
      if (formData.produtosAplicados.length === 0) {
        setMessage(t('cadastro.selectOneProduct'));
        setLoading(false);
        return;
      }
      
      // Add photo if selected
      if (selectedFile) {
        formDataToSend.append('fotoCarro', selectedFile);
      }

      const response = await api.post('/api/cadastro', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setMessage(t('cadastro.successMessage').replace('{{id}}', response.data.certificadoId));
        setFormData({
          nomeCliente: '',
          telefone: '',
          email: '',
          cpf: '',
          dataNascimento: '',
          endereco: '',
          cep: '',
          logradouro: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          modeloCarro: '',
          anoModelo: '',
          cor: '',
          placa: '',
          produtosAplicados: [],
          serialNumbers: {},
          dataAplicacao: '',
          localAplicado: '',
          estado: ''
        });
        setSelectedFile(null);
        setPreviewUrl(null);
      }
    } catch (error) {
      setMessage(t('cadastro.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cadastro-container">
      <div className="cadastro-header">
        <div className="header-nav-buttons">
          <button onClick={() => navigate('/')} className="back-btn">{t('common.back')}</button>
          <button onClick={() => navigate('/meus-cadastros')} className="back-btn meus-cadastros-btn">{t('cadastro.myCerts')}</button>
          <button onClick={() => setTutorialOpen(true)} className="back-btn tutorial-btn">{t('cadastro.tutorialButton')}</button>
        </div>
        <Link to="/" className="logo-link">
          <div className="logo-wrapper-small">
            <div className="logo-circle-small">
              <img src="/logo.png" alt="IGL Coatings Logo" />
            </div>
          </div>
        </Link>
        <h1>{t('cadastro.title')}</h1>
        <p>{t('cadastro.subtitle')}</p>
        <div className="user-info">
          <span>{t('cadastro.applicator')} {JSON.parse(localStorage.getItem('user') || '{}')?.nome || t('cadastro.loading')}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="cadastro-form">
        <div className="form-section">
          <h2>{t('cadastro.clientSection')}</h2>
          
          {/* CPF Search - First field */}
          <div className="form-row">
            <div className="form-group full-width">
              <label>{t('cadastro.cpfLabel')}</label>
              <div className="cpf-search-container">
                <input
                  type="text"
                  name="cpf"
                  value={formData.cpf}
                  onChange={(e) => {
                    handleChange(e);
                    // Reset existing client if CPF changes
                    if (clienteExistente) {
                      setClienteExistente(false);
                      setVeiculosExistentes([]);
                      setDadosBloqueados(false);
                    }
                  }}
                  placeholder={t('cadastro.cpfPlaceholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => handleCPFSearch()}
                  disabled={searchingCPF || formData.cpf.replace(/[^0-9]/g, '').length < 11}
                  className="search-cpf-btn"
                >
                  {searchingCPF ? t('cadastro.searchingCpf') : t('cadastro.searchCpf')}
                </button>
              </div>
            </div>
          </div>

          {/* Existing client banner */}
          {clienteExistente && (
            <div className="cliente-existente-banner">
              <div className="banner-content">
                <div className="banner-icon">!</div>
                <div className="banner-text">
                  <strong>{t('cadastro.clientExistsBanner')}</strong>
                  <span>{t('cadastro.clientExistsBannerText').replace('{{count}}', veiculosExistentes.length)}</span>
                </div>
                <button type="button" onClick={handleNovoCliente} className="banner-reset-btn">
                  {t('cadastro.newClient')}
                </button>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>{t('cadastro.fullName')}</label>
              <input
                type="text"
                name="nomeCliente"
                value={formData.nomeCliente}
                onChange={handleChange}
                readOnly={dadosBloqueados}
                className={dadosBloqueados ? 'input-locked' : ''}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('cadastro.phone')}</label>
              <input
                type="tel"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                readOnly={dadosBloqueados}
                className={dadosBloqueados ? 'input-locked' : ''}
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('cadastro.email')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                readOnly={dadosBloqueados}
                className={dadosBloqueados ? 'input-locked' : ''}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('cadastro.birthDate')}</label>
              <input
                type="date"
                name="dataNascimento"
                value={formData.dataNascimento}
                onChange={handleChange}
                readOnly={dadosBloqueados}
                className={dadosBloqueados ? 'input-locked' : ''}
              />
            </div>
            <div className="form-group">
              <label>{t('cadastro.state')}</label>
              <select
                name="estado"
                value={formData.estado}
                onChange={handleChange}
                disabled={dadosBloqueados}
                className={dadosBloqueados ? 'input-locked' : ''}
                required
              >
                <option value="">{t('cadastro.stateSelect')}</option>
                <option value="AC">Acre</option>
                <option value="AL">Alagoas</option>
                <option value="AP">Amapá</option>
                <option value="AM">Amazonas</option>
                <option value="BA">Bahia</option>
                <option value="CE">Ceará</option>
                <option value="DF">Distrito Federal</option>
                <option value="ES">Espírito Santo</option>
                <option value="GO">Goiás</option>
                <option value="MA">Maranhão</option>
                <option value="MT">Mato Grosso</option>
                <option value="MS">Mato Grosso do Sul</option>
                <option value="MG">Minas Gerais</option>
                <option value="PA">Pará</option>
                <option value="PB">Paraíba</option>
                <option value="PR">Paraná</option>
                <option value="PE">Pernambuco</option>
                <option value="PI">Piauí</option>
                <option value="RJ">Rio de Janeiro</option>
                <option value="RN">Rio Grande do Norte</option>
                <option value="RS">Rio Grande do Sul</option>
                <option value="RO">Rondônia</option>
                <option value="RR">Roraima</option>
                <option value="SC">Santa Catarina</option>
                <option value="SP">São Paulo</option>
                <option value="SE">Sergipe</option>
                <option value="TO">Tocantins</option>
              </select>
            </div>
          </div>
          {dadosBloqueados ? (
            <div className="form-row">
              <div className="form-group full-width">
                <label>{t('cadastro.address')}</label>
                <input
                  type="text"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleChange}
                  readOnly
                  className="input-locked"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('cadastro.cep')}</label>
                  <div className="cep-input-row">
                    <input
                      type="text"
                      name="cep"
                      value={formData.cep}
                      onChange={handleCepChange}
                      placeholder={t('cadastro.cepPlaceholder')}
                      maxLength={9}
                    />
                    <button
                      type="button"
                      className="cep-search-btn"
                      onClick={buscarCep}
                      disabled={cepLoading || formData.cep.replace(/\D/g, '').length !== 8}
                    >
                      {cepLoading ? '...' : t('common.search')}
                    </button>
                  </div>
                  {cepError && <span className="cep-error">{cepError}</span>}
                </div>
                <div className="form-group">
                  <label>{t('cadastro.numero')}</label>
                  <input
                    type="text"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    placeholder={t('cadastro.numeroPlaceholder')}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>{t('cadastro.logradouro')}</label>
                  <input
                    type="text"
                    name="logradouro"
                    value={formData.logradouro}
                    onChange={handleChange}
                    placeholder={t('cadastro.logradouroPlaceholder')}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('cadastro.complemento')}</label>
                  <input
                    type="text"
                    name="complemento"
                    value={formData.complemento}
                    onChange={handleChange}
                    placeholder={t('cadastro.complementoPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('cadastro.bairro')}</label>
                  <input
                    type="text"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t('cadastro.cidade')}</label>
                  <input
                    type="text"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* EXISTING VEHICLES SECTION */}
        {clienteExistente && veiculosExistentes.length > 0 && (
          <div className="form-section veiculos-existentes-section">
            <h2>{t('cadastro.registeredVehicles')}</h2>
            <p className="section-subtitle">{t('cadastro.registeredVehiclesHint')}</p>
            <div className="veiculos-grid">
              {veiculosExistentes.map((veiculo, index) => (
                <div key={index} className="veiculo-card">
                  <div className="veiculo-card-header">
                    <span className="veiculo-modelo">{veiculo.modeloCarro}</span>
                    <span className="veiculo-placa">{veiculo.placa}</span>
                  </div>
                  <div className="veiculo-card-body">
                    <div className="veiculo-detail">
                      <span className="veiculo-label">{t('cadastro.colorLabel')}</span>
                      <span>{veiculo.cor}</span>
                    </div>
                    <div className="veiculo-detail">
                      <span className="veiculo-label">{t('cadastro.productsLabel')}</span>
                      <span>{veiculo.produtosAplicados}</span>
                    </div>
                    <div className="veiculo-detail">
                      <span className="veiculo-label">{t('cadastro.applicationLabel')}</span>
                      <span>{veiculo.dataAplicacao}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="veiculo-select-btn"
                    onClick={() => handleSelectVeiculo(veiculo)}
                  >
                    {t('cadastro.selectVehicle')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>{clienteExistente ? t('cadastro.newVehicleSection') : t('cadastro.vehicleSection')}</h2>
          <div className="form-row">
            <div className="form-group">
              <label>{t('cadastro.plate')}</label>
              <div className="placa-search-container">
                <input
                  type="text"
                  name="placa"
                  value={formData.placa}
                  onChange={(e) => {
                    handleChange(e);
                    const raw = e.target.value;
                    setPlacaSearch(raw);
                    if (raw.replace(/[^a-zA-Z0-9]/g, '').length === 7) {
                      handlePlacaSearch(raw);
                    }
                  }}
                  placeholder={t('cadastro.platePlaceholder')}
                  maxLength={8}
                  required
                />
                <button 
                  type="button" 
                  onClick={() => handlePlacaSearch()}
                  disabled={searchingPlaca}
                  className="search-placa-btn"
                >
                  {searchingPlaca ? t('cadastro.searchingPlaca') : t('cadastro.searchPlaca')}
                </button>
              </div>
              <small className="placa-hint">{searchingPlaca ? t('cadastro.placaHintSearching') : t('cadastro.placaHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('cadastro.vehicleModel')}</label>
              <input
                type="text"
                name="modeloCarro"
                value={formData.modeloCarro}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('cadastro.yearModel')}</label>
              <input
                type="text"
                name="anoModelo"
                value={formData.anoModelo}
                onChange={handleChange}
                placeholder={t('cadastro.yearPlaceholder')}
              />
            </div>
            <div className="form-group">
              <label>{t('cadastro.color')}</label>
              <input
                type="text"
                name="cor"
                value={formData.cor}
                onChange={handleChange}
                placeholder={t('cadastro.colorPlaceholder')}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('cadastro.applicationSection')}</h2>
          <div className="form-row">
            <div className="form-group full-width">
              <label>{t('cadastro.productsApplied')}</label>
              <div className="products-selection">
                <div className="selected-products">
                  {formData.produtosAplicados.length === 0 ? (
                    <p className="no-products">{t('cadastro.noProducts')}</p>
                  ) : (
                    <div className="products-tags">
                      {formData.produtosAplicados.map(product => (
                        <div key={product} className="product-tag">
                          <span>{product}</span>
                          <button 
                            type="button" 
                            onClick={() => handleRemoveProduct(product)}
                            className="remove-tag"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="product-search-container">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={handleProductSearch}
                    onFocus={() => setShowProductSuggestions(true)}
                    placeholder={t('cadastro.searchProducts')}
                    className="product-search-input"
                  />
                  
                  {showProductSuggestions && filteredProducts.length > 0 && (
                    <div className="product-suggestions">
                      {filteredProducts.map(product => (
                        <div
                          key={product}
                          onClick={() => handleProductSelect(product)}
                          className="suggestion-item"
                        >
                          {product}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {formData.produtosAplicados.length === 0 && (
                  <p className="products-warning">{t('cadastro.productsWarning')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Serial Numbers - one per product */}
          {formData.produtosAplicados.length > 0 && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>{t('cadastro.serialNumbers')}</label>
                <div className="serial-numbers-list">
                  {formData.produtosAplicados.map(product => (
                    <div key={product} className="serial-number-item">
                      <span className="serial-product-name">{product}</span>
                      <input
                        type="text"
                        value={formData.serialNumbers[product] || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            serialNumbers: {
                              ...prev.serialNumbers,
                              [product]: e.target.value
                            }
                          }));
                        }}
                        placeholder={t('cadastro.serialPlaceholder').replace('{{product}}', product)}
                        required
                      />
                      <div className="barcode-input-wrapper">
                        <button
                          type="button"
                          className="scan-barcode-btn"
                          onClick={() => openScanner(product)}
                          title={t('cadastro.scannerTitle')}
                        >
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                            <circle cx="12" cy="13" r="4"/>
                          </svg>
                        </button>
                        <div className="help-icon" title="Como usar o scanner">
                          ?
                          <div className="help-tooltip">
                            <h4>{t('cadastro.scannerTooltipTitle')}</h4>
                            <p><strong>{t('cadastro.scannerTooltipHow')}</strong> {t('cadastro.scannerTooltipPurpose')}</p>
                            <ol>
                              <li>{t('cadastro.scannerStep1')}</li>
                              <li>{t('cadastro.scannerStep2')}</li>
                              <li>{t('cadastro.scannerStep3')}</li>
                              <li>{t('cadastro.scannerStep4')}</li>
                              <li>{t('cadastro.scannerStep5')}</li>
                            </ol>
                            <p><strong>Tip:</strong> {t('cadastro.scannerTip')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>{t('cadastro.applicationDate')}</label>
              <input
                type="date"
                name="dataAplicacao"
                value={formData.dataAplicacao}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('studios.selectStudio')}</label>
              {estudios.length === 0 ? (
                <p style={{ color: '#ff6b6b', fontSize: '14px', margin: '8px 0' }}>
                  {t('studios.noStudiosRegistered')}
                </p>
              ) : (
                <select
                  name="localAplicado"
                  value={formData.localAplicado}
                  onChange={handleChange}
                  required
                  className="studio-select"
                >
                  <option value="">{t('studios.selectPlaceholder')}</option>
                  {estudios.map(est => (
                    <option key={est.id} value={`${est.nome} - ${est.endereco}, ${est.numero}, ${est.bairro}, ${est.cidade}/${est.estado}`}>
                      {est.nome} - {est.cidade}/{est.estado}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('cadastro.photoSection')}</h2>
          <div className="photo-upload-section">
            {!previewUrl ? (
              <div className="photo-upload-area">
                <input
                  type="file"
                  id="fotoCarro"
                  name="fotoCarro"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="fotoCarro" className="photo-upload-label">
                  <div className="upload-icon">Câmera</div>
                  <div className="upload-text">
                    <p>{t('cadastro.photoUpload')}</p>
                    <span>{t('cadastro.photoFormats')}</span>
                  </div>
                </label>
              </div>
            ) : (
              <div className="photo-preview">
                <img src={previewUrl} alt="Preview do veículo" className="preview-image" />
                <button type="button" onClick={handleRemovePhoto} className="remove-photo-btn">
                  {t('cadastro.removePhoto')}
                </button>
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className="message">
            {message}
          </div>
        )}

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? t('cadastro.submitting') : t('cadastro.submit')}
        </button>
      </form>
      
      {/* Tutorial Modal */}
      {tutorialOpen && (
        <div className="scanner-modal-overlay" onClick={() => setTutorialOpen(false)}>
          <div className="scanner-modal-content tutorial-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="scanner-close-btn" onClick={() => setTutorialOpen(false)}>✕</button>
            <h3 className="scanner-title">{t('cadastro.tutorialTitle')}</h3>
            <video
              className="tutorial-video-player"
              src="/tutorial-video.mp4"
              controls
              playsInline
            />
            <ol className="tutorial-steps-list">
              <li>{t('cadastro.tutorialStep1')}</li>
              <li>{t('cadastro.tutorialStep2')}</li>
              <li>{t('cadastro.tutorialStep3')}</li>
              <li>{t('cadastro.tutorialStep4')}</li>
              <li>{t('cadastro.tutorialStep5')}</li>
              <li>{t('cadastro.tutorialStep6')}</li>
            </ol>
            <p className="scanner-hint">{t('cadastro.tutorialHint')}</p>
          </div>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      {scannerOpen && (
        <div className="scanner-modal-overlay" onClick={closeScanner}>
          <div className="scanner-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="scanner-close-btn" onClick={closeScanner}>✕</button>
            <h3 className="scanner-title">{t('cadastro.scannerTitle')}</h3>
            <p className="scanner-product-label">{scannerProduct}</p>
            <div className="scanner-viewport">
              <div id={scannerContainerId}></div>
            </div>
            {scannerError && (
              <div className="scanner-error">{scannerError}</div>
            )}
            <p className="scanner-hint">{t('cadastro.scannerHint')}</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CadastroPage;
